/**
 * SendGateModal Component
 * 
 * Hard-stop modal that appears when user tries to send/finalize
 * an estimate with unresolved blocking issues.
 * 
 * Only shows:
 * - Blocked jobs
 * - Missing required items
 * - One-click navigation to affected jobs
 * 
 * Does NOT show:
 * - Removed defaults
 * - Suggestions
 */

import React from 'react';
import { 
  XCircle, 
  AlertTriangle,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

/**
 * Main Send Gate Modal Component
 */
export const SendGateModal = ({
  isOpen,
  onClose,
  sendGateData,
  onJumpToJob,
  onRetryAfterFix
}) => {
  if (!sendGateData) return null;

  const { can_send, blockers, warnings } = sendGateData;

  // Group blockers by job
  const blockersByJob = blockers?.reduce((acc, blocker) => {
    const jobId = blocker.job_group_id;
    if (!acc[jobId]) {
      acc[jobId] = {
        jobId,
        jobTitle: blocker.job_title,
        blockers: []
      };
    }
    acc[jobId].blockers.push(blocker);
    return acc;
  }, {}) || {};

  const jobsWithBlockers = Object.values(blockersByJob);

  // If can send, don't show modal
  if (can_send) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-6 h-6" />
            Cannot Send Quote
          </DialogTitle>
          <DialogDescription>
            The following issues must be resolved before this quote can be sent.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4 max-h-[400px] overflow-y-auto">
          {jobsWithBlockers.map((job) => (
            <div 
              key={job.jobId}
              className="border border-red-200 rounded-lg overflow-hidden"
            >
              {/* Job header */}
              <div className="flex items-center justify-between p-3 bg-red-50">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="font-medium text-red-800 truncate max-w-[250px]">
                    {job.jobTitle}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onJumpToJob?.(job.jobId);
                    onClose();
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-100"
                >
                  Go to Job
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Blockers list */}
              <div className="p-3 space-y-2">
                {job.blockers.map((blocker, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-2 text-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-red-700">{blocker.blocker_message}</span>
                      {blocker.quick_fix_available && (
                        <span className="ml-2 text-xs text-green-600">
                          (Quick fix available)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Warnings section - non-blocking, informational only */}
          {warnings && warnings.length > 0 && (
            <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">
                  Additional Notes ({warnings.length})
                </span>
              </div>
              <div className="space-y-1">
                {warnings.map((warning, idx) => (
                  <div key={idx} className="text-xs text-amber-600">
                    • {warning.job_title}: {warning.message}
                  </div>
                ))}
              </div>
              <p className="text-xs text-amber-500 mt-2 italic">
                These are non-blocking and will not prevent sending.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              // Jump to first blocked job
              const firstBlockedJob = jobsWithBlockers[0];
              if (firstBlockedJob) {
                onJumpToJob?.(firstBlockedJob.jobId);
              }
              onClose();
            }}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
          >
            Fix Issues
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendGateModal;
